import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';

import { getCurrentLocationUuid } from 'selectors/session';

import { DETAIL_VIEWS, VIEWS } from 'features/notifications/componentRegistry';
import { useRequestsFeed } from 'features/notifications/hooks/useRequestsFeed';
import { RequestGroup, RequestType } from 'features/notifications/types';

import {
  clearInvalidation,
  clearSelectedRequest,
  selectInvalidatedTabs,
  selectSelectedRequest,
  setSelectedRequest,
} from '../../slice';

import { EmptyState } from './EmptyState';

const TeamRequests = () => {
  const dispatch = useDispatch();
  const currentLocation = useSelector(getCurrentLocationUuid);
  const selectedRequest = useSelector(selectSelectedRequest);
  const invalidatedTabs = useSelector(selectInvalidatedTabs);
  const prevSelectedRequestRef = useRef(selectedRequest);

  const {
    requests,
    loading,
    isFetchingMore,
    hasMore,
    sentinelRef,
    fetchInitialRequests,
  } = useRequestsFeed({
    tab: 'team',
    locationId: currentLocation,
  });

  const handleRequestClick = (request: RequestGroup, type: RequestType) => {
    dispatch(setSelectedRequest({ request, type }));
  };
  const handleClose = () => {
    dispatch(clearSelectedRequest());
  };

  useEffect(() => {
    const wasViewingDetail = prevSelectedRequestRef.current?.request;
    const isNowViewingList = !selectedRequest?.request;
    const shouldRefetch = wasViewingDetail && isNowViewingList;
    const isInvalidated = invalidatedTabs.team;

    prevSelectedRequestRef.current = selectedRequest;

    if (loading || shouldRefetch || isInvalidated) {
      fetchInitialRequests();
      if (isInvalidated) {
        dispatch(clearInvalidation('team'));
      }
    }
  }, [
    currentLocation,
    dispatch,
    selectedRequest,
    loading,
    invalidatedTabs,
    fetchInitialRequests,
  ]);

  if (loading) {
    return (
      <Box hcenter vcenter h="50vh">
        <CircularInfinite size="large" variant="dark" />
      </Box>
    );
  }

  if (requests.length === 0) return <EmptyState />;

  if (selectedRequest && selectedRequest.request) {
    const isTeamRequest = selectedRequest?.request?.viewer_role === 'oam';
    return (
      <Box h="100%">
        {DETAIL_VIEWS[selectedRequest.type as keyof typeof DETAIL_VIEWS](
          selectedRequest.request.id,
          handleClose,
          isTeamRequest,
          selectedRequest.type === 'time_off'
            ? `
            ${selectedRequest.request.requester?.first_name} ${' '}
            ${selectedRequest.request.requester?.last_name}
            `
            : undefined
        )}
      </Box>
    );
  }

  return (
    <Box scrollY>
      {requests.map(request => {
        const viewKey = request.type as keyof typeof VIEWS;
        const ViewComponent = VIEWS[viewKey];
        return (
          <Box key={request.id}>
            {/* TypeScript cannot infer union type narrowing with dynamic keys */}
            {/* @ts-expect-error - request.type guarantees correct parameter type */}
            {ViewComponent(request, handleRequestClick)}
          </Box>
        );
      })}
      {hasMore && <Box ref={sentinelRef} h={1} w="100%" />}
      {isFetchingMore && (
        <Box hcenter vcenter py={32}>
          <CircularInfinite size="medium" variant="dark" />
        </Box>
      )}
    </Box>
  );
};

export default TeamRequests;
